import { ChangeEvent, useRef, useState } from "react";
import usePopup from "../../../hooks/usePopup";
import { Button, IconButton, Tooltip, useTheme } from "@mui/material";
import CameraIcon from "@mui/icons-material/Camera";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DoneIcon from "@mui/icons-material/Done";
import ReplayIcon from "@mui/icons-material/Replay";
import moment from "moment";
const allowedFiles = ".jpg,.jpeg,.png,.avif"
export type OnCaptureParamsType = { imageUrl: string; file: File };
function CameraComponent({
  onCapture,
}: {
  onCapture?: (params: OnCaptureParamsType) => void;
}) {
  const theme = useTheme();
  const [imageUrl, setImageUrl] = useState<string>("");
  const [allMediaStream, setAllMediaStream] = useState<MediaStream[]>([]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const selectedFileRef = useRef<HTMLInputElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const { CustomModal } = usePopup();
  const [openImage, setOpenImage] = useState(false);
  const startCamera = async () => {
    try {
      setOpenImage(true);
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      setAllMediaStream((prev) => [...prev, stream]);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
    }
  };

  const handleCapture = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (canvas && video) {
      const context = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
      }
      const url = canvas.toDataURL("image/png");
      setImageUrl(url);
    }
  };

  const clearImage = () => {
    if (selectedFileRef?.current?.value) {
      selectedFileRef.current.value = "";
    }
  };

  const stopCamera = () => {
    if (allMediaStream.length) {
      allMediaStream.forEach((s) => {
        s.getTracks().forEach((track) => track.stop());
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const handleClose = () => {
    stopCamera();
    setOpenImage(false);
    setImageUrl("");
  };
  const handleConfirm = () => {
    if (
      selectedFileRef?.current?.value &&
      selectedFileRef?.current?.files?.length
    ) {
      const url = URL.createObjectURL(selectedFileRef?.current?.files[0]);
      setImageUrl(url);
      onCapture &&
        onCapture({
          imageUrl: url,
          file: selectedFileRef?.current?.files[0],
        });
      handleClose();
    } else {
      if (imageUrl) {
        const arr = imageUrl.split(",");
        const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        onCapture &&
          onCapture({
            imageUrl,
            file: new File(
              [u8arr],
              `patient-image-${moment().format("DDMMYYYYHHmmss")}`,
              {
                type: mime,
              }
            ),
          });
      }
      handleClose();
    }
  };

  const handleRetake = () => {
    if (imageUrl) {
      setImageUrl("");
      clearImage();
      startCamera();
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const url = URL.createObjectURL(e.target.files[0]);
      setImageUrl(url);
      stopCamera();
    }
  };

  return (
    <>
      <CustomModal
        title="Patient image"
        open={openImage}
        setOpen={setOpenImage}
        handleClose={handleClose}
      >
        <>
          <input
            className="hidden"
            id="selectPatientImage"
            ref={selectedFileRef}
            type="file"
            accept={allowedFiles}
            onChange={handleFileSelect}
          />
          <div>
            <img
              className={imageUrl ? "block" : "hidden"}
              src={imageUrl}
              style={{ width: "640px", height: "480px", objectFit: "contain" }}
            />
            <video
              className={imageUrl ? "hidden" : "block"}
              ref={videoRef}
              autoPlay
              playsInline
              width="640"
              height="480"
              // style={{ width: "640px", height: "480px", objectFit: "contain" }}
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>
          <div className="relative flex justify-center items-center gap-2 py-2">
            {imageUrl ? (
              <>
                <Button
                  color="success"
                  variant="text"
                  endIcon={<DoneIcon />}
                  onClick={handleConfirm}
                >
                  confirm
                </Button>{" "}
                <Button
                  color="error"
                  variant="text"
                  endIcon={<ReplayIcon />}
                  onClick={handleRetake}
                >
                  retake
                </Button>{" "}
              </>
            ) : (
              <>
                <IconButton
                  onClick={handleCapture}
                  sx={{ color: theme.palette.primary.main }}
                >
                  <CameraIcon sx={{ fontSize: 50 }} />
                </IconButton>
                <Tooltip title={`Supported file formats: ${allowedFiles}`}>
                  <Button
                    component="label"
                    htmlFor="selectPatientImage"
                    variant="outlined"
                    sx={{
                      position: "absolute",
                      right: 15,
                    }}
                  >
                    Choose File
                  </Button>
                </Tooltip>
              </>
            )}
          </div>
        </>
      </CustomModal>
      <IconButton onClick={startCamera}>
        <CameraAltIcon color="success" />
      </IconButton>
    </>
  );
}

export default CameraComponent;
